import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { Activities } from "../components/ActivityFeed";
import { Administration } from "../components/Administration";
import { Application } from "../components/Administration/Application";
import { Classification } from "../components/Administration/Classification";
import { FailureClassification } from "../components/Administration/Classification/Failures";
import { QuantityClassification } from "../components/Administration/Classification/Quantities";
import { AdministrationNavigation } from "../components/Administration/Navigation";
import {
  AdministrationProcesses,
  CacDashboard,
  ProcessesDashboard,
} from "../components/Administration/Processes";
import Users from "../components/Administration/Users";
import { ProjectHeaderNav } from "../components/App/Header/Nav";
import { ProjectName } from "../components/App/Header/Title";
import { Project, ProjectLayout } from "../components/Project";
import { ProjectDiagnosisNonIdealProxy } from "../components/Project/Diagnosis";
import { ProjectMeasuresNonIdealProxy } from "../components/Project/Measures";
import { ProjectNavigate } from "../components/Project/ProjectNavigate";
import Summary, { SummaryNavigate } from "../components/Project/Summary";
import Synthesis from "../components/Project/Summary/Synthesis";
import SynthesisDashboardLite from "../components/Project/Summary/SynthesisDashboardLite";
import { ProjectSymptomsNonIdealProxy } from "../components/Project/Symptoms";
import Projects from "../components/Projects";
import { Reporting } from "../components/Reporting";
import { ReportingDiagnosis } from "../components/Reporting/View/Diagnosis";
import { ReportingPerformance } from "../components/Reporting/View/Performance";
import { Shell } from "../components/Shell";
import { Sources } from "../components/Sources";
import t from "../i18n";
import { Dashboards } from "../components/Project/Dashboards";
import { Settings } from "../components/Project/Settings";
import { QuantitiesLayout } from "../components/Project/Settings/Quantities";
import { Simulation } from "../components/Project/Settings/Setup/Simulation";
import { ProjectSetup } from "../components/Project/Settings/Setup";
import { ProjectSetupDiagnosis } from "../components/Project/Settings/Setup/Diagnosis";
import FaultEvents from "../components/Project/Events/views/Events";
import { LiveEventPage } from "../components/Project/Events/views/Events/LiveEventView";
import { ClosedEventPage } from "../components/Project/Events/views/Events/ClosedEventView";
import { EventPage } from "../components/Project/Events/views/Event";
import { DataTab } from "../components/Project/Events/views/Event/components/Tabs/DataTab";
import { SymptomTab } from "../components/Project/Events/views/Event/components/Tabs/SymptomTab";
import { DiagnosisTab } from "../components/Project/Events/views/Event/components/Tabs/DiagnosisTab";
import { DashboardTab } from "../components/Project/Events/views/Event/FaultEvent/components/Tabs/DashboardTab";
import { CrossFilters } from "../components/Project/Settings/Quantities/view/CrossFilters";
import { Quantities } from "../components/Project/Settings/Quantities/view/Quantities";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import LoadingBoundary from "@ui/LoadingBoundary";
import { Filters } from "../components/Project/Settings/ActivationFilters";
import Exploration from "../components/Project/Exploration";
import DisplayFilters from "../components/Project/Settings/DisplayFilters";
import { RedirectUsingFaultEventId } from "../components/Project/Events/views/Event/Redirect";
import ErrorBoundary from "../components/ErrorBoundary";

const BASE_PATH = window.__webpack_public_path__;

export const routes = [
  {
    element: (
      <LoadingBoundary>
        <Shell />
      </LoadingBoundary>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // #region Root
      {
        path: "/" as const,
        element: <Navigate to="/projects" replace />,
      },
      // #endregion Root

      // #region Activity Feed
      {
        path: "/activity" as const,
        element: (
          <LoadingBoundary>
            <Activities />
          </LoadingBoundary>
        ),
        handle: {
          documentTitle: () => t("activity.title"),
          title: () => t("activity.header.title"),
        },
      },
      // #endregion Activity Feed

      // #region Administration
      {
        path: "/administration" as const,
        element: (
          <LoadingBoundary>
            <Administration />
          </LoadingBoundary>
        ),
        handle: {
          title: () => t("administration.header.title"),
          navigation: () => <AdministrationNavigation />,
        },
        children: [
          {
            path: "users" as const,
            element: (
              <LoadingBoundary>
                <Users />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () => t("users.title"),
            },
          },
          {
            path: "customization" as const,
            element: (
              <LoadingBoundary>
                <Application />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () => t("customization.title"),
            },
          },
          {
            path: "processes" as const,
            element: <AdministrationProcesses />,
            handle: {
              documentTitle: () => t("administration.processes.title"),
            },
            children: [
              {
                index: true,
                element: <Navigate to="monitoring" replace />,
              },
              {
                path: "monitoring" as const,
                element: (
                  <LoadingBoundary>
                    <ProcessesDashboard />
                  </LoadingBoundary>
                ),
                handle: {
                  documentTitle: () => t("administration.processes.title"),
                },
              },
              {
                path: "trigger" as const,
                element: (
                  <LoadingBoundary>
                    <CacDashboard />
                  </LoadingBoundary>
                ),
                handle: {
                  documentTitle: () => t("customization.section.cac.title"),
                },
              },
            ],
          },
          {
            path: "classification" as const,
            element: (
              <LoadingBoundary>
                <Classification />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () => t("classification.title"),
            },
            children: [
              {
                path: "quantities" as const,
                element: (
                  <LoadingBoundary>
                    <QuantityClassification />
                  </LoadingBoundary>
                ),
              },
              {
                path: "failures" as const,
                element: (
                  <LoadingBoundary>
                    <FailureClassification />
                  </LoadingBoundary>
                ),
              },
            ],
          },
        ],
      },
      // #endregion Administration

      // #region Reporting
      {
        path: "/reporting" as const,
        element: <Reporting />,
        handle: {
          documentTitle: () => t("reporting.header.title"),
          title: () => t("reporting.header.title"),
        },
        children: [
          {
            path: "performance" as const,
            element: (
              <LoadingBoundary>
                <ReportingPerformance />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () =>
                `${t("reporting.header.title")} | ${t("reporting.page.performance")}`,
              title: () => t("reporting.header.title"),
            },
          },
          {
            path: "diagnosis" as const,
            element: (
              <LoadingBoundary>
                <ReportingDiagnosis />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () =>
                `${t("reporting.header.title")} | ${t("reporting.page.diagnosis")}`,
              title: () => t("reporting.header.title"),
            },
          },
        ],
      },
      // #endregion Reporting

      // #region Source
      {
        path: "/sources" as const,
        element: (
          <LoadingBoundary>
            <Sources />
          </LoadingBoundary>
        ),
        handle: {
          documentTitle: () => t("sources.title"),
          title: () => t("sources.header.title"),
        },
      },
      // #endregion Source

      // #region Projects
      {
        path: "/projects" as const,
        children: [
          {
            index: true,
            element: (
              <LoadingBoundary>
                <Projects />
              </LoadingBoundary>
            ),
            handle: {
              documentTitle: () => t("projects.title"),
              title: () => t("projects.header.title"),
            },
          },
          // #region Project
          {
            path: ":projectId" as const,
            element: (
              <LoadingBoundary>
                <Project />
              </LoadingBoundary>
            ),
            handle: {
              title: () => (
                <Suspense>
                  <ProjectName />
                </Suspense>
              ),
              navigation: () => (
                <Suspense>
                  <ProjectHeaderNav />
                </Suspense>
              ),
            },
            children: [
              {
                element: (
                  <LoadingBoundary>
                    <ProjectLayout />
                  </LoadingBoundary>
                ),
                // #region Settings
                path: "settings" as const,
                handle: {
                  projectDocumentTitle: (name: string) =>
                    name + " | " + t("project.document.title.settings"),
                },
                children: [
                  {
                    index: true,
                    element: (
                      <ErrorBoundary>
                        <LoadingBoundary>
                          <Settings />
                        </LoadingBoundary>
                      </ErrorBoundary>
                    ),
                  },
                  {
                    children: [
                      {
                        path: "quantities" as const,
                        element: (
                          <LoadingBoundary>
                            <QuantitiesLayout />
                          </LoadingBoundary>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <LoadingBoundary>
                                <Quantities />
                              </LoadingBoundary>
                            ),
                          },
                          {
                            path: "crossFilters" as const,
                            element: (
                              <LoadingBoundary>
                                <CrossFilters />
                              </LoadingBoundary>
                            ),
                          },
                        ],
                      },
                      {
                        path: "filters" as const,
                        element: (
                          <LoadingBoundary>
                            <Filters />
                          </LoadingBoundary>
                        ),
                        handle: {
                          projectDocumentTitle: (name: string) =>
                            name + " | " + t("project.document.title.settings"),
                        },
                      },
                      {
                        path: "display-filters" as const,
                        element: (
                          <LoadingBoundary>
                            <DisplayFilters />
                          </LoadingBoundary>
                        ),
                        handle: {
                          projectDocumentTitle: (name: string) =>
                            name + " | " + t("project.document.title.settings"),
                        },
                      },
                    ],
                  },
                ],
              },
              // #endregion Settings
              // #region Fault Events
              {
                path: "fault-events" as const,
                handle: {
                  projectDocumentTitle: (name: string) =>
                    name + " | " + t("project.document.title.fault_events"),
                },
                children: [
                  {
                    path: "" as const, // index: true does not work here
                    element: (
                      <ErrorBoundary>
                        <LoadingBoundary>
                          <FaultEvents />
                        </LoadingBoundary>
                      </ErrorBoundary>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <LoadingBoundary>
                            <LiveEventPage />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "closed" as const,
                        element: (
                          <LoadingBoundary>
                            <ClosedEventPage />
                          </LoadingBoundary>
                        ),
                      },
                    ],
                  },
                  {
                    path: ":failureName/:startsAt" as const,
                    element: <RedirectUsingFaultEventId />,
                    children: [
                      {
                        path: "*" as const,
                        element: <RedirectUsingFaultEventId />,
                      },
                      // {
                      //   path: "symptom" as const,
                      //   element: (
                      //     <LoadingBoundary>
                      //       <SymptomTab />
                      //     </LoadingBoundary>
                      //   ),
                      // },
                      // {
                      //   path: "data" as const,
                      //   element: (
                      //     <LoadingBoundary>
                      //       <DataTab />
                      //     </LoadingBoundary>
                      //   ),
                      // },
                      // {
                      //   path: "dashboard/:dashboardId" as const,
                      //   element: (
                      //     <LoadingBoundary>
                      //       <DashboardTab />
                      //     </LoadingBoundary>
                      //   ),
                      // },
                    ],
                  },
                  {
                    path: ":faultEventId" as const,
                    element: (
                      <LoadingBoundary>
                        <EventPage />
                      </LoadingBoundary>
                    ),
                    children: [
                      {
                        path: "fault" as const,
                        element: (
                          <LoadingBoundary>
                            <DiagnosisTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "symptom" as const,
                        element: (
                          <LoadingBoundary>
                            <SymptomTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "data" as const,
                        element: (
                          <LoadingBoundary>
                            <DataTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "dashboard/:dashboardId" as const,
                        element: (
                          <LoadingBoundary>
                            <DashboardTab />
                          </LoadingBoundary>
                        ),
                      },
                    ],
                  },

                  {
                    path: "manual/:manualFaultEventId" as const,
                    element: (
                      <LoadingBoundary>
                        <EventPage />
                      </LoadingBoundary>
                    ),
                    children: [
                      {
                        path: "fault" as const,
                        element: (
                          <LoadingBoundary>
                            <DiagnosisTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "symptom" as const,
                        element: (
                          <LoadingBoundary>
                            <SymptomTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "data" as const,
                        element: (
                          <LoadingBoundary>
                            <DataTab />
                          </LoadingBoundary>
                        ),
                      },
                      {
                        path: "dashboard/:dashboardId" as const,
                        element: (
                          <LoadingBoundary>
                            <DashboardTab />
                          </LoadingBoundary>
                        ),
                      },
                    ],
                  },
                ],
              },
              // #endregion Fault Events
              // #region Dashboards
              {
                path: "dashboards" as const,
                element: (
                  <ErrorBoundary>
                    <LoadingBoundary>
                      <Dashboards />
                    </LoadingBoundary>
                  </ErrorBoundary>
                ),
                handle: {
                  projectDocumentTitle: (name: string) =>
                    name + " | " + t("project.document.title.dashboards"),
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { default: Component } = await import(
                        "../components/Project/Dashboards/view/Dashboards"
                      );
                      return { Component };
                    },
                  },
                  {
                    path: ":dashboardId" as const,
                    lazy: async () => {
                      const { default: Component } = await import(
                        "../components/Project/Dashboards/view/Dashboard"
                      );
                      return { Component };
                    },
                  },
                  {
                    path: ":dashboardId/edit" as const,
                    lazy: async () => {
                      const { default: Component } = await import(
                        "../components/Project/Dashboards/view/Dashboard"
                      );
                      return { Component };
                    },
                  },
                ],
              },
              // #endregion Dashboards
              {
                element: (
                  <LoadingBoundary>
                    <ProjectLayout />
                  </LoadingBoundary>
                ),
                children: [
                  { index: true, element: <ProjectNavigate /> },
                  {
                    path: "setup" as const,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="../settings" replace />,
                      },
                      {
                        path: ":setupId" as const,
                        element: (
                          <LoadingBoundary>
                            <ProjectSetup />
                          </LoadingBoundary>
                        ),
                        children: [
                          {
                            index: true,
                            element: <Navigate to="simulation" replace />,
                          },
                          {
                            path: "simulation" as const,
                            element: (
                              <LoadingBoundary>
                                <Simulation />
                              </LoadingBoundary>
                            ),
                            handle: {
                              projectDocumentTitle: (name: string) =>
                                name + " | " + t("project.document.title.simulation"),
                            },
                          },
                          {
                            path: "diagnosis" as const,
                            element: (
                              <LoadingBoundary>
                                <ProjectSetupDiagnosis />
                              </LoadingBoundary>
                            ),
                            handle: {
                              projectDocumentTitle: (name: string) =>
                                name + " | " + t("project.document.title.diagnosis"),
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "summary" as const,
                    element: <Summary />,
                    errorElement: <RouteErrorBoundary />,
                    handle: {
                      projectDocumentTitle: (name: string) =>
                        name + " | " + t("project.document.title.summary"),
                    },
                    children: [
                      {
                        index: true,
                        element: <SummaryNavigate />,
                      },
                      {
                        path: "summary" as const,
                        element: (
                          <LoadingBoundary>
                            <Synthesis />
                          </LoadingBoundary>
                        ),
                      },
                      //{ path: "plant-state" as const, element: <PlantState /> },
                      {
                        path: "plant-state" as const,
                        element: <div>TODO : implement plant-state page</div>,
                      },
                      {
                        path: "dashboard/:dashboardId" as const,
                        element: (
                          <LoadingBoundary>
                            <SynthesisDashboardLite />
                          </LoadingBoundary>
                        ),
                      },
                    ],
                  },
                  {
                    path: "measures" as const,
                    element: (
                      <ErrorBoundary>
                        <LoadingBoundary>
                          <Exploration>
                            <ProjectMeasuresNonIdealProxy />
                          </Exploration>
                        </LoadingBoundary>
                      </ErrorBoundary>
                    ),
                    handle: {
                      projectDocumentTitle: (name: string) =>
                        name + " | " + t("project.document.title.measures"),
                    },
                  },
                  {
                    path: "symptoms" as const,
                    element: (
                      <LoadingBoundary>
                        <Exploration>
                          <ProjectSymptomsNonIdealProxy />
                        </Exploration>
                      </LoadingBoundary>
                    ),
                    handle: {
                      projectDocumentTitle: (name: string) =>
                        name + " | " + t("project.document.title.symptoms"),
                    },
                  },
                  {
                    path: "diagnosis" as const,
                    element: (
                      <LoadingBoundary>
                        <Exploration>
                          <ProjectDiagnosisNonIdealProxy />
                        </Exploration>
                      </LoadingBoundary>
                    ),
                    handle: {
                      projectDocumentTitle: (name: string) =>
                        name + " | " + t("project.document.title.diagnosis"),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      // #endregion Projects
    ],
  },
] satisfies RouteObject[];

export type Routes = typeof routes;

export const router = createBrowserRouter(routes, { basename: BASE_PATH });
